// Wave 3.99h — Central source of truth for portal-wide RBAC.
//
// Every entry maps a permission key (present in the backend catalog under
// ``routes/users.py::PERMISSION_GROUPS``) to a route path prefix or a
// sidebar destination. Both sidebar rendering (AppShell) and route
// wrapping (App.js via ``PermissionGate``) consume this file so that the
// gate is applied consistently.
//
// Design principles:
//   1. Super Admin + Company Admin bypass all view gates.
//   2. Anyone else must have the specific key present in their
//      ``permissions`` array (or a legacy alias that expands to it).
//   3. Dashboard is always accessible (baseline landing surface even
//      for users with an empty ``permissions`` array).
//   4. ``.view`` unchecked = the user cannot open the module at all.

// ---------------------------------------------------------------------------
// Route → permission map (used by <PermissionGate>).
// Ordered by longest prefix first so more-specific routes win. When
// evaluating, iterate from most-specific to least-specific prefix.
// ---------------------------------------------------------------------------
export const ROUTE_PERMISSIONS = [
  // Sub-routes first so they resolve before their parent prefix.
  { prefix: '/reports/memos', permission: 'reports.memos.view' },
  { prefix: '/reports/customer-gold-ledger', permission: 'reports.customer_gold.view' },
  { prefix: '/reports/unified-closing-balance', permission: 'reports.unified_closing.view' },
  // Wave 4.07 — CAD Development report.
  { prefix: '/reports/cad-development', permission: 'reports.cad.view' },
  // Production Stage Digest report.
  { prefix: '/reports/production-stage-digest', permission: 'reports.production_digest.view' },
  // Estimated Delivery report.
  { prefix: '/reports/estimated-delivery', permission: 'reports.estimated_delivery.view' },
  { prefix: '/reports', anyOf: [
    'reports.view',
    'reports.unified_closing.view',
    'reports.memos.view',
    'reports.diamond_closing.view',
    'reports.color_stone_closing.view',
    'reports.pearl_closing.view',
    'reports.gold_ledger.view',
    'reports.jewellery.view',
    'reports.cad.view',
    'reports.production_digest.view',
    'reports.estimated_delivery.view',
    'reports.customer_gold.view',
  ] },

  { prefix: '/orders/', permission: 'orders.view' },
  { prefix: '/orders', permission: 'orders.view' },
  { prefix: '/production-timeline', permission: 'orders.view' },

  { prefix: '/customers', permission: 'orders.view' },
  // Customers are effectively an ordering-context master; align with the
  // Orders module gate so a user without Orders access cannot enumerate
  // customer records either.

  // Wave 3.99l — Raw material modules now gated by the canonical
  // ``materials.<mat>.see`` keys. Legacy ``diamonds.packets.view`` /
  // ``color_stones.view`` / ``pearls.view`` / ``gold.view`` still work
  // via the alias expansion in ``lib/auth.jsx``.
  { prefix: '/diamonds', permission: 'materials.diamonds.see' },
  // Wave 3.99q — Broken Recut Issues now serves Diamond / CS+Pearl
  // buyers AND Karigar. Any of the three stone see keys unlocks the
  // page; the backend + frontend then narrow rows to the caller's
  // scope so buyers only see their own material.
  { prefix: '/broken-packet-issues', anyOf: [
    'materials.diamonds.see',
    'materials.color_stones.see',
    'materials.pearls.see',
  ] },

  { prefix: '/color-stones', permission: 'materials.color_stones.see' },
  { prefix: '/pearls', permission: 'materials.pearls.see' },
  { prefix: '/gold', permission: 'materials.gold.see' },
  // Wave 3.99z — Mountings (Semi-Finished Gold Pieces).
  { prefix: '/mountings', permission: 'materials.mountings.see' },

  { prefix: '/jewellery', permission: 'jewellery.view' },
  { prefix: '/issue-memos', permission: 'orders.view' },
  { prefix: '/return-memos', permission: 'orders.view' },

  { prefix: '/vendors', permission: 'vendors.view' },
  { prefix: '/goldsmiths', permission: 'goldsmiths.view' },
  { prefix: '/cutters', permission: 'cutters.view' },
  { prefix: '/cad-developers', permission: 'cad_developers.view' },

  { prefix: '/requests', permission: 'requests.view' },
  { prefix: '/edit-requests', permission: 'requests.view' },

  { prefix: '/logs', permission: 'logs.view' },

  // /dashboard, /login, /admin/* intentionally omitted — Dashboard is
  // free-for-all authenticated users, Login is public, and admin pages
  // are role-gated (super_admin / company_admin) separately.
];

// ---------------------------------------------------------------------------
// Sidebar nav destination → permission map. Used by AppShell to hide
// links the user is not entitled to see.
// ---------------------------------------------------------------------------
// Wave 3.99l — Reports link opens if the user has ANY per-report view
// sub-key (including the legacy master ``reports.view`` which aliases
// to all of them). Same for /reports/memos.
export const REPORT_VIEW_KEYS = [
  'reports.view',
  'reports.unified_closing.view',
  'reports.memos.view',
  'reports.diamond_closing.view',
  'reports.color_stone_closing.view',
  'reports.pearl_closing.view',
  'reports.gold_ledger.view',
  'reports.jewellery.view',
  'reports.cad.view',
  'reports.production_digest.view',
  'reports.estimated_delivery.view',
  'reports.customer_gold.view',
];

export const NAV_PERMISSIONS = {
  '/gold': 'materials.gold.see',
  // Wave 3.99z — Mountings sidebar visible with the base see permission.
  '/mountings': 'materials.mountings.see',
  '/diamonds': 'materials.diamonds.see',
  '/color-stones': 'materials.color_stones.see',
  '/pearls': 'materials.pearls.see',
  '/orders': 'orders.view',
  '/production-timeline': 'orders.view',
  '/jewellery': 'jewellery.view',
  '/vendors': 'vendors.view',
  '/customers': 'orders.view',
  '/goldsmiths': 'goldsmiths.view',
  '/cutters': 'cutters.view',
  '/cutters/ledger': 'cutters.view',
  '/cad-developers': 'cad_developers.view',
  // Wave 3.99q — Broken Recut Issues sidebar shown to any user who
  // can see at least one stone material.
  '/broken-packet-issues': { anyOf: [
    'materials.diamonds.see',
    'materials.color_stones.see',
    'materials.pearls.see',
  ] },
  '/reports': { anyOf: REPORT_VIEW_KEYS },
  '/reports/memos': 'reports.memos.view',
  '/reports/customer-gold-ledger': 'reports.customer_gold.view',
  '/reports/cad-development': 'reports.cad.view',
  '/reports/production-stage-digest': 'reports.production_digest.view',
  '/reports/estimated-delivery': 'reports.estimated_delivery.view',
  '/requests': 'requests.view',
  '/edit-requests': 'requests.view',
  '/logs': 'logs.view',
};

// Helper: find the permission required for a given route path (matches
// the most-specific known prefix). Returns null if the route is not
// gated (Dashboard, admin pages, memos etc. fall through).
export function permissionForRoute(path) {
  for (const row of ROUTE_PERMISSIONS) {
    if (path === row.prefix || path.startsWith(row.prefix + '/')) {
      return row.permission;
    }
  }
  return null;
}

// Helper: given a user, a NAV item (object with `.to` string), and the
// ``hasPermission`` function from useAuth, return whether the link
// should be visible in the sidebar.
export function canSeeNavItem(user, nav, hasPermission) {
  if (!user) return false;
  // Sales roles (Sales Person / Sales Manager) are strictly limited to the
  // Dashboard (Pending Requirements), the Order Forms page, the Production
  // Timeline (scoped to their customers' active orders), the Customers master
  // (read-only, scoped to their attributed customers) and their Notifications
  // — nothing else.
  if (user.role === 'sales_person' || user.role === 'sales_manager') {
    return nav.to === '/dashboard'
      || nav.to === '/orders'
      || nav.to === '/designs'
      || nav.to === '/production-timeline'
      || nav.to === '/customers'
      || nav.to === '/reports/memos'
      || nav.to === '/reports/digest'
      || nav.to === '/reports/production-stage-digest'
      || nav.to === '/reports/estimated-delivery'
      || nav.to === '/notifications';
  }
  const rule = NAV_PERMISSIONS[nav.to];
  if (!rule) return true; // unmapped links (Dashboard etc.) always visible
  // Wave 3.99l — Support ``{ anyOf: [...] }`` rules (used by /reports).
  if (typeof rule === 'object' && Array.isArray(rule.anyOf)) {
    return rule.anyOf.some((k) => hasPermission(k));
  }
  return hasPermission(rule);
}
